'use client';

import { useSelectedLayoutSegments } from 'next/navigation';
import { useAtomValue } from 'jotai';

import { NavItemType, GalaxySidebarSwitchTabIndex } from '@/types/nav';
import { usePathname } from '@/navigation';
import { MaybeMatch } from '@nf/utils-common/fpw/monadic';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import { galaxySidebarSwitchTabIndexAtom, marsSidebarSwitchTabIndexAtom } from '@/atoms/io';

interface TopMenuItemProps {
	href: string;
	type: NavItemType;
}

export const TopMenuItem = ({
	href, 
	className,
	type,
	...rest 
}: React.ComponentPropsWithoutRef<'div'> & TopMenuItemProps) => {
	const pathname = usePathname();
	const selectedLayoutSegments = useSelectedLayoutSegments();
	const view = useAppearanceStore(state => state.view);
	const sidebarSwitchTabIndex = useAtomValue(
		view === ViewEnum.galaxy ? galaxySidebarSwitchTabIndexAtom : marsSidebarSwitchTabIndexAtom
	);
	const isVirtualSportsPath = /(^|\/)sports\/(116|134)(\/|$)/.test(pathname);
	const isLiveViaSidebar =
		view === ViewEnum.galaxy
		&& sidebarSwitchTabIndex === GalaxySidebarSwitchTabIndex.Live
		&& isVirtualSportsPath;

	const isActive = MaybeMatch(type === NavItemType.World).fold(
		() => {
			if (type === NavItemType.Live) {
				return pathname.startsWith(href) || isLiveViaSidebar;
			}
			if (type === NavItemType.Sports) {
				return pathname.startsWith(href) && !isLiveViaSidebar;
			}
			return pathname.startsWith(href);
		},
		() => {
			// An example of the menu type route: /world/euro/t/1
			const worldIndex = selectedLayoutSegments.findIndex(
				segment => segment === 'world'
			);
			const menuType = selectedLayoutSegments[worldIndex + 1];
			return new RegExp(`^\\/world\\/${menuType}(?:\/|$)`).test(href);
		}
	);

	return (
		<div
			className={className}
			data-selected={isActive}
			{...rest}
		/>
	);
};